import React, { useState, useEffect, useCallback, useRef } from 'react';
import { XIcon, CheckCircleIcon, LoaderIcon, WifiOffIcon } from 'lucide-react';
import { useConnectWhatsApp } from '../hooks/useWhatsAppStatus';
import { useQueryClient } from '@tanstack/react-query';
import { useWhatsAppSyncProgress } from '../hooks/useWhatsAppSyncProgress';
import type { WhatsAppConnectionState } from '../types/whatsapp';

interface WhatsAppConnectionModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function WhatsAppConnectionModal({
  onClose,
  onSuccess,
}: WhatsAppConnectionModalProps) {
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<WhatsAppConnectionState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [statusHint, setStatusHint] = useState<string | null>(null);
  const [showQrTimeoutWarning, setShowQrTimeoutWarning] = useState(false);
  const [qrGenerationStartTime, setQrGenerationStartTime] = useState<number | null>(null);

  const queryClient = useQueryClient();
  const connectMutation = useConnectWhatsApp();
  const { syncState, startSync } = useWhatsAppSyncProgress();

  const hasStartedConnection = useRef(false);

  // Detect if user is on mobile device
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const initiateConnection = useCallback(async () => {
    if (hasStartedConnection.current) {
      return;
    }
    hasStartedConnection.current = true;

    const cachedStatus = queryClient.getQueryData(['whatsapp', 'status']) as any;

    if (cachedStatus?.connected) {
      setConnectionStatus('connected');
      setTimeout(() => onSuccess(), 1000);
      return;
    }

    setConnectionStatus('qr_generating');
    setQrGenerationStartTime(Date.now());
    setStatusHint('Generating QR code...');
    setErrorMessage(null);
    setQrCode(null);
    setShowQrTimeoutWarning(false);

    try {
      const result = await connectMutation.mutateAsync();
      console.log('[Modal] Connect result:', result);

      if (result.status === 'connected') {
        setConnectionStatus('connected');
        setTimeout(() => onSuccess(), 1500);
      } else if (result.qrCode || result.qr) {
        // Backend can return either qrCode or qr
        setQrCode(result.qrCode || result.qr);
        setConnectionStatus('qr_ready');
        setStatusHint(null);
      } else {
        setStatusHint('Waiting for WhatsApp to issue a QR code...');
      }
    } catch (error: any) {
      console.error('[Modal] Connect error:', error);
      setConnectionStatus('error');
      setErrorMessage(error.message || 'Failed to initiate connection');
      setStatusHint(null);
    }
  }, [connectMutation, onSuccess, queryClient]);

  // QR generation timeout warning (>10 seconds)
  useEffect(() => {
    if (connectionStatus !== 'qr_generating') return;

    const timer = setTimeout(() => {
      setShowQrTimeoutWarning(true);
      setStatusHint('QR generation is taking longer than expected...');
    }, 10000); // Show warning after 10 seconds

    return () => clearTimeout(timer);
  }, [connectionStatus]);

  // QR expiration timeout (60 seconds)
  useEffect(() => {
    if (connectionStatus !== 'qr_ready' || !qrCode) return;

    const expirationTimer = setTimeout(() => {
      setConnectionStatus('error');
      setErrorMessage('QR code expired. Please try again.');
      setQrCode(null);
    }, 60000); // 60 seconds

    return () => clearTimeout(expirationTimer);
  }, [connectionStatus, qrCode]);

  // Poll for status updates via Supabase Realtime
  useEffect(() => {
    const checkStatus = () => {
      const status = queryClient.getQueryData(['whatsapp', 'status']) as any;

      console.log('[Modal] Checking status from cache:', status, 'current connectionStatus:', connectionStatus);

      if (!status) return;

      // Detect when QR is scanned and connection is established
      if (status.connected && (connectionStatus === 'qr_ready' || connectionStatus === 'qr_scanned')) {
        console.log('[Modal] QR scanned! Starting message sync...');
        setConnectionStatus('qr_scanned');
        startSync(); // Start time-based progress tracking
        setConnectionStatus('syncing_messages');
      } else if (status.connected && connectionStatus !== 'connected' && connectionStatus !== 'syncing_messages') {
        console.log('[Modal] Detected connected status! Closing modal...');
        setConnectionStatus('connected');
        setTimeout(() => onSuccess(), 1500);
      } else if ((status.qrCode || status.qr) && (connectionStatus === 'qr_generating' || connectionStatus === 'connecting')) {
        console.log('[Modal] Detected QR code, displaying...');
        setQrCode(status.qrCode || status.qr);
        setConnectionStatus('qr_ready');
        setStatusHint(null);
      } else if (status.status === 'connecting' || status.status === 'reconnecting') {
        if (connectionStatus === 'idle') {
          setConnectionStatus('qr_generating');
        }
        setStatusHint('Connecting to WhatsApp...');
      } else if (status.error) {
        setConnectionStatus('error');
        setErrorMessage(status.error);
      }
    };

    const interval = setInterval(checkStatus, 1000);
    checkStatus(); // Run immediately
    return () => clearInterval(interval);
  }, [connectionStatus, onSuccess, queryClient, startSync]);

  // Monitor sync completion
  useEffect(() => {
    if (syncState.progressPercent >= 100 && connectionStatus === 'syncing_messages') {
      setConnectionStatus('sync_complete');
      setTimeout(() => {
        setConnectionStatus('connected');
        onSuccess();
      }, 1500);
    }
  }, [syncState.progressPercent, connectionStatus, onSuccess]);

  useEffect(() => {
    initiateConnection();
  }, [initiateConnection]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[#212121]">
            Connect WhatsApp
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-sm text-gray-600 text-center mb-6">
            {connectionStatus === 'qr_generating' && (statusHint || 'Generating QR code...')}
            {connectionStatus === 'connecting' && (statusHint || 'Connecting...')}
            {connectionStatus === 'qr_ready' && 'Scan the QR code to connect your WhatsApp'}
            {connectionStatus === 'syncing_messages' && 'Syncing your WhatsApp messages...'}
            {connectionStatus === 'sync_complete' && 'Sync complete!'}
            {connectionStatus === 'connected' && 'Connected successfully!'}
            {connectionStatus === 'error' && (errorMessage || 'Connection failed')}
          </p>

          {/* QR Code / Status Display */}
          <div className="relative mb-6">
            <div className="aspect-square bg-gray-100 rounded-2xl shadow-md flex items-center justify-center border-2 border-gray-200">
              {/* QR Generating State */}
              {connectionStatus === 'qr_generating' && (
                <div className="flex flex-col items-center justify-center p-6">
                  <LoaderIcon className="w-16 h-16 text-[#5B8E87] animate-spin mb-4" />
                  <p className="text-sm text-gray-600 text-center mb-2">
                    {showQrTimeoutWarning
                      ? 'QR generation taking longer than expected...'
                      : 'Generating QR code...'}
                  </p>
                  {showQrTimeoutWarning && (
                    <p className="text-xs text-gray-500 text-center">
                      This can take up to 2 minutes. Please don't close this window.
                    </p>
                  )}
                </div>
              )}

              {/* Loading State (connecting) */}
              {connectionStatus === 'connecting' && (
                <div className="flex flex-col items-center justify-center">
                  <LoaderIcon className="w-16 h-16 text-[#5B8E87] animate-spin mb-4" />
                  <p className="text-sm text-gray-600 text-center">
                    {statusHint || 'Connecting...'}
                  </p>
                </div>
              )}

              {/* QR Code */}
              {connectionStatus === 'qr_ready' && qrCode && (
                <div className="w-64 h-64 flex items-center justify-center">
                  <img
                    src={qrCode}
                    alt="WhatsApp QR Code"
                    className="w-full h-full rounded-xl"
                  />
                </div>
              )}

              {/* Message Syncing State */}
              {connectionStatus === 'syncing_messages' && (
                <div className="flex flex-col items-center justify-center p-6">
                  <LoaderIcon className="w-16 h-16 text-[#5B8E87] animate-spin mb-4" />
                  <p className="text-lg font-semibold text-gray-900 mb-2">
                    Syncing Messages
                  </p>
                  <p className="text-sm text-gray-600 text-center mb-4">
                    {syncState.progressPercent < 95
                      ? `${Math.round(syncState.progressPercent)}% complete`
                      : 'Almost done...'}
                  </p>
                  <div className="w-full max-w-xs bg-gray-200 rounded-full h-2 mb-4">
                    <div
                      className="bg-[#5B8E87] h-2 rounded-full transition-all duration-500"
                      style={{ width: `${syncState.progressPercent}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mb-4">
                    Estimated time: {Math.ceil(syncState.estimatedRemaining / 1000)}s remaining
                  </p>
                  <button
                    onClick={onClose}
                    className="mt-2 text-sm text-gray-600 hover:text-gray-800 underline"
                  >
                    Continue in background
                  </button>
                </div>
              )}

              {/* Sync Complete State */}
              {connectionStatus === 'sync_complete' && (
                <div className="flex flex-col items-center justify-center">
                  <CheckCircleIcon className="w-24 h-24 text-[#5B8E87] mb-4" />
                  <p className="text-lg font-semibold text-[#5B8E87]">
                    Sync Complete
                  </p>
                </div>
              )}

              {/* Connected State */}
              {connectionStatus === 'connected' && (
                <div className="flex flex-col items-center justify-center">
                  <CheckCircleIcon className="w-24 h-24 text-[#5B8E87] mb-4" />
                  <p className="text-lg font-semibold text-[#5B8E87]">
                    Connected Successfully
                  </p>
                </div>
              )}

              {/* Error State */}
              {connectionStatus === 'error' && (
                <div className="flex flex-col items-center justify-center p-6">
                  <WifiOffIcon className="w-16 h-16 text-red-500 mb-4" />
                  <p className="text-sm font-semibold text-red-600 mb-2">Connection Failed</p>
                  <p className="text-xs text-gray-600 text-center">{errorMessage}</p>
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          {connectionStatus === 'qr_ready' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="text-sm font-medium text-[#212121] mb-3 text-center">
                How to scan:
              </div>

              {isMobileDevice ? (
                // Mobile Device Warning - Cannot scan own screen
                <div className="space-y-3">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-start">
                      <span className="text-amber-600 text-lg mr-2">⚠️</span>
                      <div className="text-xs text-gray-700">
                        <div className="font-semibold text-amber-700 mb-1">
                          You're on a mobile device
                        </div>
                        <p className="mb-2">
                          To connect WhatsApp, you need to scan this QR code from another device.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <span className="text-blue-600 font-bold mr-2">💡</span>
                    <div className="text-xs text-gray-700">
                      <div className="font-semibold mb-2">Recommended options:</div>
                      <ol className="list-decimal ml-4 space-y-2">
                        <li>
                          <span className="font-semibold">Open on desktop/laptop</span>
                          <br />
                          <span className="text-gray-600">
                            Access this page from a computer, then scan with your phone
                          </span>
                        </li>
                        <li>
                          <span className="font-semibold">Use another device</span>
                          <br />
                          <span className="text-gray-600">
                            Display this QR on a tablet/another phone, then scan with your phone's camera
                          </span>
                        </li>
                      </ol>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-gray-500 text-center">
                    ⏱️ QR code expires in 60 seconds
                  </div>
                </div>
              ) : (
                // Desktop Instructions
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="text-blue-600 font-bold mr-2">💬</span>
                    <div className="text-xs text-gray-700">
                      <div className="font-semibold mb-1">Open this application on your desktop / laptopand use WhatsApp app:</div>
                      <ol className="list-decimal ml-4 space-y-1">
                        <li>Open <span className="font-semibold">WhatsApp</span> on your phone</li>
                        <li>Go to <span className="font-semibold">Settings</span> → <span className="font-semibold">Linked Devices</span></li>
                        <li>Tap <span className="font-semibold">Link a Device</span></li>
                        <li>Scan the QR code above</li>
                      </ol>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-gray-500 text-center">
                    ⏱️ QR code expires in 60 seconds
                  </div>
                </div>
              )}
            </div>
          )}

          {connectionStatus === 'error' && (
            <p className="text-xs text-gray-500 text-center mb-6">
              Tap retry to generate a fresh QR. Ensure WhatsApp Web is active on your phone.
            </p>
          )}

          {/* Retry Button (Error State) */}
          {connectionStatus === 'error' && (
            <button
              onClick={() => {
                hasStartedConnection.current = false;
                initiateConnection();
              }}
              className="w-full bg-[#5B8E87] text-white font-semibold py-4 rounded-xl shadow-md hover:bg-[#4F7A74] transition-colors"
            >
              Retry Connection
            </button>
          )}

          {/* Done Button (Connected State) */}
          {connectionStatus === 'connected' && (
            <button
              onClick={onSuccess}
              className="w-full bg-[#5B8E87] text-white font-semibold py-4 rounded-xl shadow-md hover:bg-[#4F7A74] transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
